import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../api/client'
import { Badge, Loading } from '../../components/ui'

interface AdminJob {
  id: string
  title: string
  description: string
  reward: number
  status: string
  isInstant: boolean
  createdAt: string
  poster: {
    id: string
    name: string
    email: string
  }
  _count: {
    applications: number
    submissions: number
  }
}

interface JobsResponse {
  jobs: AdminJob[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState<AdminJob[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [editingJob, setEditingJob] = useState<AdminJob | null>(null)
  const [editForm, setEditForm] = useState({ title: '', reward: 0, status: '' })
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    fetchJobs()
  }, [search, statusFilter, page])

  const fetchJobs = async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (statusFilter) params.append('status', statusFilter)
      params.append('page', String(page))
      params.append('limit', '20')

      const data = await api.get<JobsResponse>(`/admin/jobs?${params}`)
      setJobs(data.jobs)
      setTotalPages(data.totalPages)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs')
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (job: AdminJob) => {
    setEditingJob(job)
    setEditForm({
      title: job.title,
      reward: job.reward,
      status: job.status,
    })
  }

  const handleSaveEdit = async () => {
    if (!editingJob) return

    if (!editForm.title.trim() || editForm.title.length < 5) {
      setError('Title must be at least 5 characters')
      return
    }
    if (editForm.reward < 0) {
      setError('Reward cannot be negative')
      return
    }

    setActionLoading(true)
    setError('')
    try {
      await api.patch(`/admin/jobs/${editingJob.id}`, editForm)
      setEditingJob(null)
      await fetchJobs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update job')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (jobId: string, jobTitle: string) => {
    if (!confirm(`Are you sure you want to delete job "${jobTitle}"? This action cannot be undone.`)) {
      return
    }
    setActionLoading(true)
    setError('')
    try {
      await api.delete(`/admin/jobs/${jobId}`)
      await fetchJobs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete job')
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusVariant = (status: string): 'green' | 'blue' | 'gold' | 'gray' | 'red' => {
    switch (status) {
      case 'OPEN': return 'green'
      case 'IN_PROGRESS': return 'blue'
      case 'COMPLETED': return 'gold'
      case 'CANCELLED': return 'red'
      default: return 'gray'
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <Link to="/admin" className="text-secondary" style={{ fontSize: '14px' }}>Admin</Link>
          <h1>Job Management</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="card-static" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="input"
            placeholder="Search by title..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            style={{ flex: 1, minWidth: 200 }}
          />
          <select
            className="input select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            style={{ width: 150 }}
          >
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {isLoading ? (
        <Loading message="Loading jobs..." />
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <h3>No jobs found</h3>
        </div>
      ) : (
        <>
          <div className="card-static" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Job</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Poster</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Reward</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Apps/Subs</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '13px' }}>Created</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem', fontSize: '13px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{job.title}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {job.isInstant && <Badge variant="blue">Instant</Badge>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontSize: '13px' }}>{job.poster.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{job.poster.email}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <Badge variant={getStatusVariant(job.status)}>{job.status.replace('_', ' ')}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                      {job.reward} pts
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      {job._count.applications} / {job._count.submissions}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '13px' }}>
                      {new Date(job.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Link to={`/jobs/${job.id}`} className="btn btn-sm">View</Link>
                        <button className="btn btn-sm" onClick={() => handleEdit(job)} disabled={actionLoading}>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ color: 'var(--error)' }}
                          onClick={() => handleDelete(job.id, job.title)}
                          disabled={actionLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem' }}>
              <button className="pagination-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>
                &lt;
              </button>
              <span style={{ padding: '0 1rem' }}>Page {page} of {totalPages}</span>
              <button className="pagination-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                &gt;
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card-static" style={{ width: '100%', maxWidth: 400 }}>
            <h3 style={{ marginBottom: '1rem' }}>Edit Job: {editingJob.title}</h3>
            <div className="form-group">
              <label className="label">Title</label>
              <input
                type="text"
                className="input"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Reward (USDC)</label>
              <input
                type="number"
                className="input"
                value={editForm.reward}
                onChange={(e) => setEditForm({ ...editForm, reward: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div className="form-group">
              <label className="label">Status</label>
              <select
                className="input select"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditingJob(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
